import React from "react";
//scss
import "flatpickr/dist/flatpickr.css";
import "choices.js/public/assets/styles/choices.min.css";
import "./assets/scss/hope-ui.scss";
import "./assets/scss/pro.scss";
import "./assets/scss/custom.scss";
import "./assets/scss/dark.scss";
import "./assets/scss/rtl.scss";
import "./assets/scss/customizer.scss";
import "./assets/custom/scss/custom.scss";
import InactivityHandler from "./components/partials/InactivityHandler";
import setupAxiosInterceptors from "./components/partials/axiosConfig";

// Redux Selector / Action
import { useDispatch } from "react-redux";

// import state selectors
import { setSetting } from "./store/setting/actions";

// axios interceptors are setup once when app loads
setupAxiosInterceptors();

function App({ children }) {
  const dispatch = useDispatch();
  dispatch(setSetting());

  React.useEffect(() => {
    // Dynamic table header-body width synchronization
    const syncTableWidths = () => {
      // Find all report table wrappers
      const wrappers = document.querySelectorAll('.report-page .ant-table-wrapper, .table-responsive .ant-table-wrapper');
      wrappers.forEach((wrapper) => {
        const bodyTable = wrapper.querySelector('.ant-table-body table, .ant-table-content table');
        const headerTable = wrapper.querySelector('.ant-table-sticky-holder table, .ant-table-header table');
        
        if (bodyTable && headerTable && bodyTable !== headerTable) {
          // Force both to use table-layout: fixed
          bodyTable.style.tableLayout = 'fixed';
          headerTable.style.tableLayout = 'fixed';
          
          // Get the actual width of the body table
          const bodyWidth = bodyTable.getBoundingClientRect().width;
          if (bodyWidth > 0) {
            headerTable.style.setProperty('width', `${bodyWidth}px`, 'important');
            headerTable.style.setProperty('min-width', `${bodyWidth}px`, 'important');
          }
          
          // Sync colgroup col element widths
          const bodyCols = bodyTable.querySelectorAll('colgroup col');
          const headerCols = headerTable.querySelectorAll('colgroup col');
          
          if (bodyCols.length > 0 && headerCols.length > 0 && bodyCols.length === headerCols.length) {
            // Find the first row in tbody to measure actual cell widths
            const firstRow = bodyTable.querySelector('.ant-table-tbody > tr:not(.ant-table-placeholder)');
            
            bodyCols.forEach((col, index) => {
              let cellWidth = 0;
              if (firstRow && firstRow.children[index]) {
                cellWidth = firstRow.children[index].getBoundingClientRect().width;
              }
              
              // Fallback to inline style or attribute width if row not available/rendered
              if (cellWidth <= 0) {
                const colWidthStyle = col.style.width || col.getAttribute('width');
                if (colWidthStyle) {
                  cellWidth = parseFloat(colWidthStyle);
                }
              }
              
              if (cellWidth > 0) {
                const widthStr = `${cellWidth}px`;
                col.style.setProperty('width', widthStr, 'important');
                if (headerCols[index]) {
                  headerCols[index].style.setProperty('width', widthStr, 'important');
                }
              }
            });
          }
        }
      });
    };

    // Set up ResizeObserver to watch for size changes in any table containers
    const observer = new ResizeObserver(() => {
      syncTableWidths();
    });

    const observeElements = () => {
      const bodies = document.querySelectorAll(
        '.report-page .ant-table-body, .report-page .ant-table-content, .table-responsive .ant-table-body, .table-responsive .ant-table-content'
      );
      bodies.forEach((el) => observer.observe(el));
    };

    // Initial sync
    syncTableWidths();
    observeElements();

    // Set up MutationObserver to watch for newly added tables in the DOM
    const mutationObserver = new MutationObserver(() => {
      syncTableWidths();
      observeElements();
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Also run on window resize
    window.addEventListener('resize', syncTableWidths);

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', syncTableWidths);
    };
  }, []);

  return <div className="App">{children}<InactivityHandler /></div>;
}

export default App;
